import { KEY_RE, NAME_RE, type ClientMsg, type LeaderRow, type NameStatus, type PlayerStats, type RoomInfo, type ServerMsg } from "../shared/protocol";
import { Vector2 } from "three";
import { Game } from "./game";
import { Hud } from "./hud";
import { SignEditor } from "./signedit";
import { dirtBackground, planksBackground, stoneBackground } from "./textures";
import { FONT } from "./font";
import { loadFont } from "./font";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

await loadFont();

const canvas = $<HTMLCanvasElement>("gl");
const lobby = $("lobby");
const nameInput = $<HTMLInputElement>("name");
const pauseEl = $("pause");
const clickToPlay = $("clicktoplay");
const isTouch = matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
const params = new URLSearchParams(location.search);

lobby.style.backgroundImage = `url(${dirtBackground()})`;
const planks = planksBackground();

let ws: WebSocket | null = null;
// Inside a tweet the iframe may not be allowed pointer lock. Then the mouse works like a
// finger in Pocket Edition: drag to look, click on what you want. No crosshair.
let dragLook = false;
function setDragLook() {
  dragLook = true;
  clickToPlay.hidden = true;
  document.body.classList.add("freelook");
}
const send = (m: ClientMsg) => {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
};

if (isTouch) document.body.classList.add("touch");
drawLogo($<HTMLCanvasElement>("logo-main"), "SURVIVAL GAMES");
const SPLASHES = ["Pocket Edition!", "Also try Minecraft!", "May the odds be ever in your favor!", "100% more pedestals!", "Now with chests!", "Herobrine removed!", "LBSG forever!", "Don't dig straight down!", "Touch controls!", "As seen on X!"];
$("splash").textContent = SPLASHES[Math.floor(Math.random() * SPLASHES.length)]!;

const hud = new Hud();
hud.touch = isTouch;
const signEditor = new SignEditor();
const game = new Game(canvas, send, hud, signEditor);
game.touchMode = isTouch;
hud.onSlot = (i) => game.selectSlot(i);

// ---------- username ----------

function randomName() {
  return `Steve${Math.floor(Math.random() * 9000 + 1000)}`;
}
let stored = "";
try {
  stored = localStorage.getItem("sg-name") ?? "";
} catch {}
nameInput.value = NAME_RE.test(stored) ? stored : randomName();
nameInput.addEventListener("input", () => {
  nameInput.value = nameInput.value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 16);
});
function currentName() {
  const n = nameInput.value.trim();
  if (!NAME_RE.test(n)) return null;
  try {
    localStorage.setItem("sg-name", n);
  } catch {}
  return n;
}

// The first device to play a name owns it: a random key made once and kept here.
// If storage is blocked we send none, so we never claim a name with a key we'll lose.
let deviceKey: string | undefined;
try {
  deviceKey = localStorage.getItem("sg-key") ?? undefined;
  if (!deviceKey || !KEY_RE.test(deviceKey)) {
    deviceKey = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");
    localStorage.setItem("sg-key", deviceKey);
  }
} catch {
  deviceKey = undefined;
}

const nameNote = $("namenote");
function showNameTaken() {
  nameNote.textContent = "That name belongs to someone else — pick another";
  nameNote.className = "taken";
  nameNote.hidden = false;
  nameInput.classList.add("taken");
}

/** Under the name box: your stats if the name is yours, a warning if it's someone else's. */
let meSeq = 0;
async function refreshMe() {
  const name = currentName();
  const seq = ++meSeq;
  nameInput.classList.remove("taken");
  if (!name) {
    nameNote.hidden = true;
    return;
  }
  try {
    const r = await fetch("/api/me", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name, key: deviceKey }) });
    const me = (await r.json()) as { status?: NameStatus; stats?: PlayerStats | null };
    if (seq !== meSeq) return; // they kept typing
    if (me.status === "taken") return showNameTaken();
    const s = me.stats;
    nameNote.className = "";
    nameNote.textContent = s ? `Wins ${s.wins} · Kills ${s.kills} · Deaths ${s.deaths}` : "";
    nameNote.hidden = !s || s.games + s.kills + s.deaths === 0;
  } catch {}
}
let meTimer = 0;
nameInput.addEventListener("input", () => {
  clearTimeout(meTimer);
  meTimer = window.setTimeout(refreshMe, 350);
});
refreshMe();

// ---------- lobby sign wall ----------

function renderRooms(rooms: RoomInfo[]) {
  const wall = $("signwall");
  wall.innerHTML = "";
  for (const r of rooms) {
    const el = document.createElement("div");
    el.className = "joinsign";
    el.style.backgroundImage = `url(${planks})`;
    const waiting = r.phase === "waiting" || r.phase === "countdown";
    const phase = r.phase === "waiting" ? "Waiting" : r.phase === "countdown" ? "Starting" : r.phase === "ended" ? "Restarting" : "In game";
    el.innerHTML = `<div class="s-head">[Join]</div><div>${r.name}</div><div>${r.players}/${r.max}</div><div class="s-phase ${waiting ? "waiting" : "ingame"}">${phase}</div>`;
    el.addEventListener("click", () => join(r.id));
    wall.appendChild(el);
  }
}

function join(world: string) {
  const name = currentName();
  if (!name) {
    nameInput.focus();
    nameInput.style.borderColor = "#f55";
    return;
  }
  send({ t: "join", world, name, key: deviceKey });
}

// ---------- leaderboard ----------

async function refreshLeaderboard() {
  const board = $("leaderboard");
  try {
    const rows = (await (await fetch("/api/leaderboard")).json()) as LeaderRow[];
    board.innerHTML = "";
    rows.forEach((r, i) => {
      const el = document.createElement("div");
      el.className = "topsign";
      el.style.backgroundImage = `url(${planks})`;
      el.title = r.name;
      el.innerHTML = `<div class="s-head">[Top] #${i + 1}</div><div></div><div>W ${r.wins} · K ${r.kills}</div>`;
      el.children[1]!.textContent = r.name;
      board.appendChild(el);
    });
    board.hidden = rows.length === 0;
  } catch {}
}
refreshLeaderboard();
setInterval(() => {
  if (!lobby.hidden) refreshLeaderboard();
}, 15000);

// ---------- worlds: one per posted link ----------

/** Opened from a post's /w/<id> link: show that world's join screen instead of the sign wall. */
const linkedWorld = params.get("w");
let currentWorld = "";

if (linkedWorld) {
  $("publicgames").hidden = true;
  $("worldjoin").hidden = false;
  $("wj-play").addEventListener("click", () => join(linkedWorld));
  const refresh = async () => {
    if (lobby.hidden) return;
    try {
      const r = await fetch(`/api/worlds/${encodeURIComponent(linkedWorld)}`);
      if (!r.ok) {
        $("wj-name").textContent = "This world doesn't exist";
        $("wj-play").hidden = true;
        return;
      }
      const w = (await r.json()) as { name: string; players: number; phase: string };
      $("wj-name").textContent = `Join "${w.name}"`;
      const state = w.phase === "waiting" || w.phase === "countdown" ? "waiting for the next round" : "round in progress";
      $("wj-info").textContent = `${w.players} playing · ${state}`;
    } catch {}
  };
  refresh();
  setInterval(refresh, 3000);
}

const shareEl = $("share");
const linkInput = $<HTMLInputElement>("cw-link");
let sharedWorld = "";

function showShare(mode: "create" | "invite", url = "", worldId = "") {
  shareEl.hidden = false;
  $("cw-err").textContent = "";
  $("share-title").textContent = mode === "create" ? "Create a world" : "Invite friends";
  $("share-create").hidden = mode !== "create";
  $("share-link").hidden = mode === "create";
  $("cw-play").hidden = mode !== "create" || !worldId;
  linkInput.value = url;
  sharedWorld = worldId;
  if (document.pointerLockElement) document.exitPointerLock();
  if (mode === "create") setTimeout(() => $("cw-name").focus(), 30);
}

$("create-btn").addEventListener("click", () => showShare("create"));
$("cw-close").addEventListener("click", () => {
  shareEl.hidden = true;
  if (game.active) {
    canvas.focus();
    lock();
  }
});
$("cw-go").addEventListener("click", async () => {
  const name = $<HTMLInputElement>("cw-name").value;
  const r = await fetch("/api/worlds", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name }) });
  const body = (await r.json()) as { id?: string; url?: string; error?: string };
  if (!r.ok || !body.id) {
    $("cw-err").textContent = body.error ?? "Couldn't create a world";
    return;
  }
  showShare("invite", body.url, body.id);
  $("share-title").textContent = "Your world is ready";
  $("cw-play").hidden = false;
});
$("cw-name").addEventListener("keydown", (e) => {
  if (e.key === "Enter") $("cw-go").click();
});
$("cw-copy").addEventListener("click", async () => {
  try {
    await navigator.clipboard.writeText(linkInput.value);
  } catch {
    // Clipboard API is often blocked inside embeds; select it so a long-press/Cmd+C works.
    linkInput.select();
    document.execCommand?.("copy");
  }
  $("cw-copy").textContent = "Copied!";
  setTimeout(() => ($("cw-copy").textContent = "Copy link"), 1500);
});
$("cw-tweet").addEventListener("click", () => {
  const text = "Survival Games, Pocket Edition style. Tap Play and fight me:";
  window.open(`https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(linkInput.value)}`, "_blank", "noopener");
});
$("cw-play").addEventListener("click", () => {
  shareEl.hidden = true;
  if (sharedWorld) join(sharedWorld);
});

function showLobby() {
  lobby.hidden = false;
  pauseEl.hidden = true;
  clickToPlay.hidden = true;
  if (document.pointerLockElement) document.exitPointerLock();
  refreshMe();
  refreshLeaderboard();
}

// ---------- network ----------

let autoJoin = params.get("room");

function connect() {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/ws`);
  ws.onopen = () => send({ t: "hello", name: currentName() ?? randomName(), key: deviceKey });
  ws.onmessage = (ev) => {
    const m = JSON.parse(ev.data) as ServerMsg;
    switch (m.t) {
      case "welcome":
        game.myId = m.id;
        if (autoJoin) {
          join(autoJoin);
          autoJoin = null;
        }
        break;
      case "rooms":
        renderRooms(m.rooms);
        break;
      case "nameTaken":
        showNameTaken();
        nameInput.focus();
        break;
      case "joined":
        game.myName = m.me;
        currentWorld = m.world;
        shareEl.hidden = true;
        lobby.hidden = true;
        game.load(m);
        if (!isTouch && !document.pointerLockElement && !dragLook) clickToPlay.hidden = false;
        if (isTouch) document.body.classList.add("freelook");
        break;
      default:
        game.handle(m);
    }
  };
  ws.onclose = () => {
    if (game.active) {
      game.unload();
      hud.toast("Disconnected");
    }
    showLobby();
    $("signwall").innerHTML = `<div style="color:#aaa">Connecting...</div>`;
    setTimeout(connect, 1500);
  };
}
connect();

// ---------- title logo ----------

/**
 * Title-screen logo: rasterise the text tiny, then rebuild every pixel as a
 * stone block with a dark extruded side — the way the Minecraft logo is made.
 */
function drawLogo(c: HTMLCanvasElement, text: string) {
  const mask = document.createElement("canvas");
  const mg = mask.getContext("2d", { willReadFrequently: true })!;
  mg.font = `11px ${FONT}`;
  const w = Math.ceil(mg.measureText(text).width) + 2;
  mask.width = w;
  mask.height = 13;
  mg.font = `11px ${FONT}`;
  mg.textBaseline = "top";
  mg.fillText(text, 1, 1);
  const data = mg.getImageData(0, 0, w, 13).data;
  const on = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < 13 && data[(y * w + x) * 4 + 3]! > 110;

  const P = 4, D = 3;
  c.width = w * P + D * P;
  c.height = 13 * P + D * P;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  const stone = new Image();
  stone.onload = () => {
    for (let d = D; d >= 1; d--) {
      g.fillStyle = d === D ? "#141414" : "#2c2c2c";
      for (let y = 0; y < 13; y++) for (let x = 0; x < w; x++) if (on(x, y)) g.fillRect(x * P + d * P * 0.5, y * P + d * P * 0.5, P, P);
    }
    for (let y = 0; y < 13; y++) for (let x = 0; x < w; x++) {
      if (!on(x, y)) continue;
      // Each letter pixel shows a 4x4 patch of the stone texture.
      g.drawImage(stone, (x * 4) % 16, (y * 4) % 16, 4, 4, x * P, y * P, P, P);
      if (!on(x, y - 1)) {
        g.fillStyle = "rgba(255,255,255,0.35)";
        g.fillRect(x * P, y * P, P, 1);
      }
      if (!on(x - 1, y)) {
        g.fillStyle = "rgba(255,255,255,0.18)";
        g.fillRect(x * P, y * P, 1, P);
      }
    }
  };
  stone.src = stoneBackground();
}

// ---------- keyboard ----------


let lastW = 0;
window.addEventListener("keydown", (e) => {
  if (!game.active || signEditor.isOpen || document.activeElement instanceof HTMLInputElement) return;
  if (["Space", "ArrowUp", "ArrowDown", "Tab"].includes(e.code)) e.preventDefault();
  if (hud.inventoryOpen) {
    if (e.code === "KeyE" || e.code === "Escape") $("inv-close").click();
    return;
  }
  if (hud.chatLogOpen) {
    if (e.code === "Escape" || e.code === "KeyT" || e.code === "Enter") $("cl-close").click();
    return;
  }
  if (e.code === "KeyW" && !e.repeat) {
    if (performance.now() - lastW < 280) game.sprint = true;
    lastW = performance.now();
  }
  if (e.code.startsWith("Digit")) {
    const n = Number(e.code.slice(5));
    if (n >= 1 && n <= 8) game.selectSlot(n - 1);
  }
  if (e.code === "KeyE") {
    hud.openInventory();
    return;
  }
  if (e.code === "F5") {
    e.preventDefault(); // not a page reload
    cycleView();
    return;
  }
  // T / Enter / slash open the chat screen, which is just the log.
  if (e.code === "KeyT" || e.code === "Enter" || e.code === "Slash") {
    e.preventDefault();
    hud.openChatLog();
    return;
  }
  game.keys.add(e.code);
});
window.addEventListener("keyup", (e) => game.keys.delete(e.code));
window.addEventListener("blur", () => game.keys.clear());

// ---------- mouse ----------

let dragging: { x: number; y: number; moved: number; button: number; drew: boolean } | null = null;
const SENS = 0.0026;
const embedded = window.self !== window.top;
let clickLockFails = 0;
let pendingLock: { fromClick: boolean } | null = null;

/**
 * Grab the mouse. Browsers refuse when there's no fresh click (closing a screen
 * with Esc doesn't count, and Chrome makes you wait a moment after Esc), so a
 * refusal just brings back "Click to play" instead of leaving a loose cursor.
 * Only a click that still can't lock means the embed forbids it: then drag-look.
 */
function lock(fromClick = false) {
  if (dragLook || isTouch || !game.active) return;
  const attempt = (pendingLock = { fromClick });
  try {
    const p = canvas.requestPointerLock() as unknown as Promise<void> | undefined;
    p?.catch?.(() => lockFailed(attempt));
  } catch {
    lockFailed(attempt);
  }
}
function lockFailed(attempt = pendingLock) {
  if (!attempt || attempt !== pendingLock) return; // promise + pointerlockerror both report the same failure
  pendingLock = null;
  if (attempt.fromClick && (embedded || ++clickLockFails >= 3)) return setDragLook();
  if (game.active && !signEditor.isOpen && !hud.inventoryOpen && !hud.chatLogOpen && pauseEl.hidden && shareEl.hidden) clickToPlay.hidden = false;
}

clickToPlay.addEventListener("click", () => {
  clickToPlay.hidden = true;
  canvas.focus();
  lock(true);
});

const ndcOf = (x: number, y: number) => {
  const r = canvas.getBoundingClientRect();
  return new Vector2(((x - r.left) / r.width) * 2 - 1, -((y - r.top) / r.height) * 2 + 1);
};

canvas.addEventListener("contextmenu", (e) => e.preventDefault());
canvas.addEventListener("mousedown", (e) => {
  if (!game.active || isTouch) return;
  canvas.focus();
  if (document.pointerLockElement === canvas) {
    if (e.button === 0) game.attack();
    // Right button: use, or with a bow, hold to draw and let go to fire.
    if (e.button === 2) game.useDown();
    return;
  }
  if (!dragLook) {
    clickToPlay.hidden = true;
    lock(true);
    return;
  }
  dragging = { x: e.clientX, y: e.clientY, moved: 0, button: e.button, drew: false };
  if (e.button === 2 && game.holding() === "bow") {
    game.useDown(ndcOf(e.clientX, e.clientY));
    dragging.drew = true;
  }
});
window.addEventListener("mousemove", (e) => {
  if (document.pointerLockElement === canvas) {
    game.look(e.movementX * SENS, e.movementY * SENS);
    return;
  }
  if (dragLook && !isTouch) game.aim = ndcOf(e.clientX, e.clientY);
  if (dragging) {
    const dx = e.clientX - dragging.x, dy = e.clientY - dragging.y;
    dragging.moved += Math.abs(dx) + Math.abs(dy);
    dragging.x = e.clientX;
    dragging.y = e.clientY;
    game.look(dx * SENS * 1.6, dy * SENS * 1.6);
  }
});
window.addEventListener("mouseup", (e) => {
  if (e.button === 2 && document.pointerLockElement === canvas) game.release();
  // Drag-look: the arrow goes wherever the cursor is when you let go.
  if (dragging?.drew) game.release(ndcOf(e.clientX, e.clientY));
  else if (dragging && dragging.moved < 6) {
    // Like tapping in Pocket Edition: act on whatever is under the cursor.
    const at = ndcOf(e.clientX, e.clientY);
    if (dragging.button === 0) game.attack(at);
    if (dragging.button === 2) game.use(at);
  }
  dragging = null;
});
canvas.addEventListener("wheel", (e) => {
  if (!game.active) return;
  e.preventDefault();
  game.selectSlot((game.slot + (e.deltaY > 0 ? 1 : 7)) % 8);
}, { passive: false });

document.addEventListener("pointerlockerror", () => lockFailed());
document.addEventListener("pointerlockchange", () => {
  const locked = document.pointerLockElement === canvas;
  // The crosshair only exists while the mouse is captured; otherwise you'd see it and a cursor.
  document.body.classList.toggle("locked", locked);
  if (locked) {
    pendingLock = null;
    clickLockFails = 0;
    pauseEl.hidden = true;
    clickToPlay.hidden = true;
    game.aim = null;
    return;
  }
  const busy = signEditor.isOpen || hud.inventoryOpen || hud.chatLogOpen || !shareEl.hidden;
  if (game.active && !busy && !isTouch && !dragLook) pauseEl.hidden = false;
});
signEditor.onClose = () => {
  canvas.focus();
  lock();
};
$("inv-close").addEventListener("click", () => {
  canvas.focus();
  lock();
});

$("menu-btn").addEventListener("click", () => {
  if (document.pointerLockElement) document.exitPointerLock();
  pauseEl.hidden = false;
});
// Third person: F5 or the camera button cycles first person → behind → facing you.
function cycleView() {
  game.cycleView();
  try {
    localStorage.setItem("sg-view", String(game.view));
  } catch {}
}
try {
  game.view = Math.max(0, Math.min(2, Number(localStorage.getItem("sg-view")) || 0));
} catch {}
$("cam-btn").addEventListener("click", () => {
  cycleView();
  if (game.active && !isTouch && !dragLook) lock(true);
});
$("chat-btn").addEventListener("click", () => (hud.chatLogOpen ? $("cl-close").click() : hud.openChatLog()));
$("cl-close").addEventListener("click", () => {
  canvas.focus();
  lock();
});

// Auto-jump: on by default, like Pocket Edition 0.12.
const autoJumpBtn = $("autojump");
const setAutoJump = (on: boolean) => {
  game.autoJump = on;
  autoJumpBtn.textContent = `Auto-Jump: ${on ? "ON" : "OFF"}`;
  try {
    localStorage.setItem("sg-autojump", on ? "1" : "0");
  } catch {}
};
let savedAutoJump: string | null = null;
try {
  savedAutoJump = localStorage.getItem("sg-autojump");
} catch {}
setAutoJump(savedAutoJump !== "0");
autoJumpBtn.addEventListener("click", () => setAutoJump(!game.autoJump));

// Sound: on by default; browsers keep it silent until the first tap or key.
const soundBtn = $("soundbtn");
const setSound = (on: boolean) => {
  game.sound.setEnabled(on);
  soundBtn.textContent = `Sound: ${on ? "ON" : "OFF"}`;
  try {
    localStorage.setItem("sg-sound", on ? "1" : "0");
  } catch {}
};
let savedSound: string | null = null;
try {
  savedSound = localStorage.getItem("sg-sound");
} catch {}
setSound(savedSound !== "0");
soundBtn.addEventListener("click", () => setSound(!game.sound.enabled));
for (const ev of ["pointerdown", "keydown", "touchend"]) window.addEventListener(ev, () => game.sound.unlock(), { capture: true });
// The Minecraft button click, on every menu button and join sign.
document.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).closest?.(".mcbtn, .pebtn, .joinsign, .slot")) game.sound.play("click");
}, { capture: true });
$("invite").addEventListener("click", () => {
  pauseEl.hidden = true;
  showShare("invite", `${location.origin}/w/${currentWorld}`);
});
$("resume").addEventListener("click", () => {
  pauseEl.hidden = true;
  canvas.focus();
  lock(true);
});
$("quit").addEventListener("click", () => {
  send({ t: "leave" });
  game.unload();
  showLobby();
});

// ---------- touch: Pocket Edition 0.12 scheme ----------
// D-pad bottom-left (sneak in the middle, diagonals appear while walking forward),
// jump bottom-right, drag anywhere to look, tap to hit/use, hold to break.

if (isTouch) {
  $("touch").hidden = false;
  const dpad = $("dpad");
  const fingers = new Map<number, string>();
  let lastSneakTap = 0;
  let sneakHeld = false;

  const apply = () => {
    const held = new Set(fingers.values());
    const up = held.has("up") || held.has("ul") || held.has("ur");
    game.touchMove.f = (up ? 1 : 0) - (held.has("down") ? 1 : 0);
    game.touchMove.s = (held.has("right") || held.has("ur") ? 1 : 0) - (held.has("left") || held.has("ul") ? 1 : 0);
    dpad.classList.toggle("fwd", up);
    sneakHeld = held.has("sneak");
    for (const b of dpad.querySelectorAll<HTMLElement>(".dp")) b.classList.toggle("on", held.has(b.dataset.k!));
  };

  // Which d-pad cell is under this point? Lets a finger slide between arrows.
  const cellAt = (x: number, y: number): string | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>(".dp");
    if (!el || !dpad.contains(el) || getComputedStyle(el).visibility === "hidden") return null;
    return el.dataset.k ?? null;
  };
  dpad.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    dpad.setPointerCapture(e.pointerId);
    const k = cellAt(e.clientX, e.clientY);
    if (!k) return;
    if (k === "sneak") {
      const now = performance.now();
      if (now - lastSneakTap < 300) {
        game.sneakToggle = !game.sneakToggle;
        dpad.querySelector(".mid")!.classList.toggle("toggled", game.sneakToggle);
      }
      lastSneakTap = now;
    }
    fingers.set(e.pointerId, k);
    apply();
  });
  dpad.addEventListener("pointermove", (e) => {
    if (!fingers.has(e.pointerId)) return;
    const k = cellAt(e.clientX, e.clientY);
    if (k && k !== "sneak" && k !== fingers.get(e.pointerId)) {
      fingers.set(e.pointerId, k);
      apply();
    }
  });
  const release = (e: PointerEvent) => {
    fingers.delete(e.pointerId);
    apply();
  };
  dpad.addEventListener("pointerup", release);
  dpad.addEventListener("pointercancel", release);

  const jump = $("t-jump");
  jump.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    jump.setPointerCapture(e.pointerId);
    jump.classList.add("on");
    game.touchMove.jump = true;
  });
  const jumpOff = () => {
    jump.classList.remove("on");
    game.touchMove.jump = false;
  };
  jump.addEventListener("pointerup", jumpOff);
  jump.addEventListener("pointercancel", jumpOff);

  // Hold the sneak button to crouch; double-tap locks it.
  const sneakTick = () => {
    game.keys.delete("ShiftLeft");
    if (sneakHeld) game.keys.add("ShiftLeft");
    requestAnimationFrame(sneakTick);
  };
  sneakTick();

  // The world: drag to look, tap to act, hold to break.
  const ndc = ndcOf;
  const looks = new Map<number, { x: number; y: number; sx: number; sy: number; moved: number; t: number; held: boolean; timer: number; drawTimer: number; draw: boolean }>();
  canvas.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse" || !game.active) return;
    const l = { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, moved: 0, t: performance.now(), held: false, timer: 0, drawTimer: 0, draw: false };
    // Holding a bow: press and hold still to draw it (a quick tap still hits, a drag still looks).
    if (game.holding() === "bow") l.drawTimer = window.setTimeout(() => {
      if (l.moved < 12) l.draw = game.startDraw();
    }, 150);
    l.timer = window.setTimeout(() => {
      if (l.moved < 12 && !l.draw) {
        l.held = true;
        game.hold(ndc(l.sx, l.sy));
      }
    }, 380);
    looks.set(e.pointerId, l);
  });
  canvas.addEventListener("pointermove", (e) => {
    const l = looks.get(e.pointerId);
    if (!l) return;
    const dx = e.clientX - l.x, dy = e.clientY - l.y;
    l.moved += Math.abs(dx) + Math.abs(dy);
    l.x = e.clientX;
    l.y = e.clientY;
    game.look(dx * 0.0075, dy * 0.0075);
  });
  const end = (e: PointerEvent) => {
    const l = looks.get(e.pointerId);
    if (!l) return;
    clearTimeout(l.timer);
    clearTimeout(l.drawTimer);
    // Let go of a drawn bow: the arrow flies toward wherever the finger is now.
    if (l.draw) game.release(ndc(l.x, l.y));
    else if (!l.held && l.moved < 12 && performance.now() - l.t < 380) game.tap(ndc(l.sx, l.sy));
    looks.delete(e.pointerId);
  };
  canvas.addEventListener("pointerup", end);
  canvas.addEventListener("pointercancel", end);
}

// ---------- loop ----------

const ro = new ResizeObserver(() => game.resize());
ro.observe(canvas);
game.resize();

let last = performance.now();
function loop(now: number) {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  game.frame(dt);
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);

// Handy for poking at the game from devtools.
(window as unknown as { sg: Game }).sg = game;
